<script setup lang="ts">
import { ref, onMounted, watch, onUnmounted, getCurrentInstance } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useUIStore } from "@/stores/ui";
import { fetchFileTicket } from "@/utils/secureFile";
import AppErrorBoundary from "@/components/shared/AppErrorBoundary.vue";
import ToastContainer from "@/components/shared/ToastContainer.vue";
import ReConsentModal from "@/components/shared/ReConsentModal.vue";
import { WifiOff } from "@lucide/vue";

const authStore = useAuthStore();
const ui = useUIStore();

const isDarkMode = ref(localStorage.getItem("sora_dark_mode") === "true");

watch(
  isDarkMode,
  (val) => {
    if (val) {
      document.documentElement.setAttribute("data-theme", "dark");
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
    localStorage.setItem("sora_dark_mode", val ? "true" : "false");
  },
  { immediate: true },
);

const route = useRoute();

// Set document.title from route meta
watch(
  () => route.meta?.title as string | undefined,
  (title) => {
    document.title = title ? `${title} | Sora Recruit` : "Sora Recruit";
  },
  { immediate: true },
);

// Global error boundary
const app = getCurrentInstance()?.appContext?.app;
if (app) {
  app.config.errorHandler = (err: unknown, _vm: unknown, info: string) => {
    ui.error("Terjadi kesalahan", info);
    console.error("Global error:", err, info);
  };
}

// Unhandled promise rejection handler
function handleUnhandledRejection(event: PromiseRejectionEvent) {
  ui.error(
    "Kesalahan tak terduga",
    event.reason?.message || "Promise rejection",
  );
  console.error("Unhandled rejection:", event.reason);
}
window.addEventListener("unhandledrejection", handleUnhandledRejection);

// Optional: Online/offline detection
const isOnline = ref(navigator.onLine);
function handleOnline() {
  isOnline.value = true;
}
function handleOffline() {
  isOnline.value = false;
}
window.addEventListener("online", handleOnline);
window.addEventListener("offline", handleOffline);

// Bootstrap: restore session from httpOnly refresh cookie.
onMounted(() => {
  authStore.initAuth();
});

onUnmounted(() => {
  window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  window.removeEventListener("online", handleOnline);
  window.removeEventListener("offline", handleOffline);
});

let ticketInterval: number | null = null;

watch(
  () => authStore.isAuthenticated,
  (isAuth) => {
    if (isAuth) {
      fetchFileTicket();
      ticketInterval = window.setInterval(() => {
        if (authStore.isAuthenticated) fetchFileTicket();
      }, 45000);
    } else if (ticketInterval) {
      clearInterval(ticketInterval);
      ticketInterval = null;
    }
  },
  { immediate: true },
);
</script>

<template>
  <!-- Offline banner -->
  <div
    v-if="!isOnline"
    class="fixed top-0 left-0 right-0 z-9999 bg-warn-bg text-warn-text border-b border-warn/20 px-4 py-2 text-xs font-bold text-center flex items-center justify-center gap-2"
  >
    <WifiOff class="size-4" />
    Tidak ada koneksi internet. Beberapa fitur mungkin tidak tersedia.
  </div>

  <AppErrorBoundary>
    <RouterView v-slot="{ Component }">
      <Transition name="page" mode="default">
        <component :is="Component" />
      </Transition>
    </RouterView>
  </AppErrorBoundary>
  <ToastContainer />
  <ReConsentModal />
</template>

<style>
.page-enter-active,
.page-leave-active {
  transition: opacity 0.15s ease;
}
.page-enter-from,
.page-leave-to {
  opacity: 0;
}
</style>
